import { set } from './utils'

function generateData (timers) {
  return Object.keys(timers).reduce((acc, cur) => {
    return set(
      cur,
      {
        isRunning: false,
        time: timers[cur].time || 0,
        instance: null
      },
      acc
    )
  }, {})
}

function setTimer (repeat) {
  return repeat ? setInterval : setTimeout
}

function clearTimer (repeat) {
  return repeat ? clearInterval : clearTimeout
}

function generateTimer (options, vm) {
  return setTimer(options.repeat)(() => {
    vm.$emit('timer-tick:' + options.name)
    options.callback()
    vm['timers'][options.name].tick++
    if (options.repeat_limit > 0 && vm['timers'][options.name].tick === options.repeat_limit) {
      vm.$timer.stop(options.name)
    }
  }, options.time)
}

function normalizeConfig (config, vm) {
  if (process.env.NODE_ENV !== 'production') {
    if (!config.name) {
      throw new Error('[vue-timers.create] name is required')
    }
    if (!config.callback && typeof vm[config.name] !== 'function') {
      throw new ReferenceError(
        '[vue-timers.create] Cannot find method ' + config.name
      )
    }
    if (config.callback && typeof config.callback !== 'function') {
      throw new TypeError(
        '[vue-timers.create] Timer callback should be a function, ' +
        typeof config.callback +
        ' given'
      )
    }
  }
  return {
    name: config.name,
    time: config.time || 0,
    repeat: 'repeat' in config ? config.repeat : false,
    repeat_limit: config.repeat_limit || 0,
    immediate: 'immediate' in config ? config.immediate : false,
    autostart: 'autostart' in config ? config.autostart : false,
    isSwitchTab: 'isSwitchTab' in config ? config.isSwitchTab : false,
    callback: (config.callback && config.callback.bind(vm)) || vm[config.name]
  }
}

function normalizeOptions (options, vm) {
  return Array.isArray(options)
    ? options.reduce((res, config) => {
      return set(config.name, normalizeConfig(config, vm), res)
    }, {})
    : Object.keys(options).reduce((res, key) => {
      return set(
        key,
        normalizeConfig(set('name', key, options[key]), vm),
        res
      )
    }, {})
}

export default {
  data () {
    if (!this.$options.timers) return {}
    this.$options.timers = normalizeOptions(this.$options.timers, this)
    return {
      timers: generateData(this.$options.timers)
    }
  },

  created () {
    if (!this.$options.timers) return
    const vm = this
    const data = vm.timers
    const options = vm.$options.timers
    vm.$timer = {
      start (name) {
        if (process.env.NODE_ENV !== 'production' && !(name in options)) {
          throw new ReferenceError(
            '[vue-timers.start] Cannot find timer ' + name
          )
        }
        if (data[name].isRunning) return
        data[name].isRunning = true
        data[name].instance = generateTimer(
          set('time', data[name].time, options[name]),
          vm
        )
        if (options[name].immediate) {
          vm.$emit('timer-tick:' + name)
          options[name].callback()
        }
        vm.$emit('timer-start:' + name)
        data[name].tick = 0
      },

      stop (name) {
        if (process.env.NODE_ENV !== 'production' && !(name in options)) {
          throw new ReferenceError(
            '[vue-timers.stop] Cannot find timer ' + name
          )
        }
        if (!data[name].isRunning) return
        clearTimer(options[name].repeat)(data[name].instance)
        data[name].isRunning = false
        vm.$emit('timer-stop:' + name)
      },
      restart (name) {
        if (process.env.NODE_ENV !== 'production' && !(name in options)) {
          throw new ReferenceError(
            '[vue-timers.restart] Cannot find timer ' + name
          )
        }
        this.stop(name)
        this.start(name)
        vm.$emit('timer-restart:' + name)
      }
    }
  },

  mounted () {
    if (!this.$options.timers) return
    const options = this.$options.timers
    Object.keys(options).forEach((key) => {
      if (options[key].autostart) {
        this.$timer.start(key)
      }
    })
  },

  activated () {
    if (!this.$options.timers) return
    const data = this.timers
    const options = this.$options.timers
    Object.keys(options).forEach((key) => {
      if (options[key].isSwitchTab && data[key].instance) {
        this.$timer.start(key)
      }
    })
  },

  deactivated () {
    if (!this.$options.timers) return
    const vm = this
    const data = vm.timers
    const options = vm.$options.timers
    Object.keys(options).forEach((key) => {
      if (options[key].isSwitchTab && data[key].instance) {
        vm.$timer.stop(key)
      }
    })
  },

  beforeDestroy () {
    if (!this.$options.timers) return
    const vm = this
    Object.keys(vm.$options.timers).forEach((key) => {
      vm.$timer.stop(key)
    })
  }
}
